import { useCallback, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AccountActionDialog, type AccountTarget } from "./AccountActionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ApiError, banUser, revokeBans } from "@/lib/api";
import { captureBan, createSubmitLock } from "./action-model";
import { LegacyBanReview } from "./LegacyBanReview";

type Action = { kind: "ban" | "revoke"; id: string; name: string | null; reason?: string; hours?: number };

export function ManageBansDialog({ target, onClose }: { target: AccountTarget; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState("");
  const [changed, setChanged] = useState(false);
  const [result, setResult] = useState("");
  const [legacyPending, setLegacyPending] = useState(false);
  const [legacyChanged, setLegacyChanged] = useState(false);
  const legacyState = useCallback((pending: boolean, draft: boolean) => {
    setLegacyPending(pending); setLegacyChanged(draft);
  }, []);
  const submitting = useRef(createSubmitLock());
  const qc = useQueryClient();
  const invalidDuration = hours.trim() !== "" && (!Number.isFinite(Number(hours)) || Number(hours) <= 0);
  const mutation = useMutation({
    mutationFn: (action: Action) => action.kind === "ban"
      ? banUser(action.id, action.reason, action.hours, action.name)
      : revokeBans(action.id, action.name),
    onSuccess: (_data, action) => {
      void qc.invalidateQueries({ queryKey: ["player-bans", action.id] });
      for (const key of ["flags", "flag-player", "recent-accounts", "audit"]) void qc.invalidateQueries({ queryKey: [key] });
      setResult(action.kind === "ban" ? "Backend ban recorded." : "All backend bans revoked.");
      setChanged(false);
    },
    onSettled: () => { submitting.current.end(); },
  });

  function submit(kind: Action["kind"]) {
    if (legacyPending || (kind === "ban" && invalidDuration) || !submitting.current.begin()) return;
    mutation.reset(); setResult("");
    mutation.mutate(captureBan(target, kind, reason, hours));
  }

  return <AccountActionDialog title="Manage bans" scope="Backend bans and individual legacy bans are reviewed separately for this player ID." target={target} pending={mutation.isPending || legacyPending} changed={changed || legacyChanged} onClose={onClose}>
    <div className="space-y-4">
      <h3 className="font-medium">Backend bans</h3>
      <p className="text-xs text-muted-foreground">These actions apply to accounts recorded in our backend.</p>
      <fieldset disabled={mutation.isPending || legacyPending} className="space-y-4">
      <label className="block space-y-1 text-sm">Reason (optional)
        <Input autoFocus value={reason} onChange={(e) => { setReason(e.target.value); setChanged(true); setResult(""); }} placeholder="cheating / harassment / ..." />
      </label>
      <label className="block space-y-1 text-sm">Duration in hours (blank = permanent)
        <Input value={hours} onChange={(e) => { setHours(e.target.value); setChanged(true); setResult(""); }} placeholder="72" type="number" min="0" step="any" />
      </label>
      {invalidDuration && <p className="text-sm text-destructive">Enter a positive duration or leave blank for a permanent ban.</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="destructive" onClick={() => submit("ban")} disabled={invalidDuration || mutation.isPending}>
          {mutation.isPending ? "Working…" : "Ban in backend"}</Button>
        <Button variant="secondary" onClick={() => submit("revoke")} disabled={mutation.isPending}>
          Revoke all backend bans</Button>
      </div>
      </fieldset>
      {mutation.error && <Alert variant="destructive"><AlertDescription>{mutation.error instanceof ApiError && mutation.error.status === 404
        ? "This account was not found in our backend. Use the separate legacy ban review below for a legacy-only account."
        : String(mutation.error)}</AlertDescription></Alert>}
      {result && <Alert><AlertDescription>{result}</AlertDescription></Alert>}
      <LegacyBanReview target={target} disabled={mutation.isPending} onState={legacyState} />
    </div>
  </AccountActionDialog>;
}
